import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analytics.service';
import {
  EngagementMetrics,
  TransferFrequencyData,
  KpiQueryParams,
  CompanyInfo,
  MonthlyCompanyEngagement,
} from '../../types/kpi.types';
import { LineChart, BarChart, MetricCard, MultiLineChart, MultiCompanyLineChart } from '../Charts';
import { Loader } from '../Loader';

interface EngagementUsageProps {
  queryParams: KpiQueryParams;
}

export const EngagementUsage: React.FC<EngagementUsageProps> = ({
  queryParams,
}) => {
  const [metrics, setMetrics] = useState<EngagementMetrics | null>(null);
  const [mauTrend, setMauTrend] = useState<any[]>([]);
  const [frequencyData, setFrequencyData] = useState<
    TransferFrequencyData[]
  >([]);
  const [monthlyEngagementData, setMonthlyEngagementData] = useState<MonthlyCompanyEngagement[]>([]);
  const [aggregatedTrends, setAggregatedTrends] = useState<Array<{ month: string; avgTransfersPerUser: number; totalVelocity: number }>>([]);
  const [averageAmountByTransferNumber, setAverageAmountByTransferNumber] = useState<Array<{ transferNumber: number; averageAmount: number; userCount: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const formatMonth = (month: string): string => {
    const [year, m] = month.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(m) - 1]} '${year.slice(2)}`;
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [engagement, trend, frequency, monthlyData, aggregated, avgAmountByTransfer] = await Promise.all([
          analyticsService.getEngagementMetrics(queryParams),
          analyticsService.getMauTrend(queryParams),
          analyticsService.getTransferFrequencyDistribution(queryParams),
          // Get all companies data (ignore company filter for multi-company trends)
          analyticsService.getMonthlyCompanyEngagement({
            startDate: queryParams.startDate,
            endDate: queryParams.endDate,
            database: queryParams.database,
            // Don't pass companyId - we want all companies
          }),
          analyticsService.getMonthlyAggregatedTrends({
            startDate: queryParams.startDate,
            endDate: queryParams.endDate,
            database: queryParams.database,
            // Don't pass companyId - we want all companies aggregated
          }),
          analyticsService.getAverageAmountByTransferNumber(queryParams),
        ]);
        setMetrics(engagement);
        setMauTrend(trend);
        setFrequencyData(frequency);
        setMonthlyEngagementData(monthlyData);
        setAggregatedTrends(aggregated);
        setAverageAmountByTransferNumber(avgAmountByTransfer);
      } catch (err: any) {
        setError(err.message || 'Failed to load engagement data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [queryParams.startDate, queryParams.endDate, queryParams.database, queryParams.companyId]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <div style={{ color: 'red' }}>Error: {error}</div>;
  }

  if (!metrics) {
    return <div>No data available</div>;
  }

  // Filter monthly data by selected company (from global selector)
  const filteredMonthlyData = queryParams.companyId
    ? monthlyEngagementData.filter(d => d.companyId.toString() === queryParams.companyId)
    : monthlyEngagementData;
  
  // Get selected company name
  const selectedCompanyName = queryParams.companyId && filteredMonthlyData.length > 0
    ? filteredMonthlyData[0].companyName
    : undefined;

  // Calculate monthly totals (aggregate across all companies when "all" is selected)
  const monthlyTotalsMap = new Map<string, {
    month: string;
    totalMau: number;
    totalTransfers: number;
    totalAmount: number;
    avgTransfersPerUser: number;
    avgTransferAmount: number;
  }>();

  filteredMonthlyData.forEach(row => {
    if (!monthlyTotalsMap.has(row.month)) {
      monthlyTotalsMap.set(row.month, {
        month: row.month,
        totalMau: 0,
        totalTransfers: 0,
        totalAmount: 0,
        avgTransfersPerUser: 0,
        avgTransferAmount: 0,
      });
    }
    const totals = monthlyTotalsMap.get(row.month)!;
    totals.totalMau += row.monthlyActiveUsers;
    totals.totalTransfers += row.totalTransfers;
    totals.totalAmount += row.totalAmount;
  });

  // Calculate averages for totals
  monthlyTotalsMap.forEach((totals, month) => {
    const monthRows = filteredMonthlyData.filter(d => d.month === month);
    const companyCount = monthRows.length;
    if (companyCount > 0) {
      totals.avgTransfersPerUser = totals.totalMau > 0 
        ? Math.round((totals.totalTransfers / totals.totalMau) * 100) / 100 
        : 0;
      totals.avgTransferAmount = totals.totalTransfers > 0
        ? Math.round((totals.totalAmount / totals.totalTransfers) * 100) / 100
        : 0;
    }
  });

  const monthlyTotals = Array.from(monthlyTotalsMap.values())
    .sort((a, b) => a.month.localeCompare(b.month));

  // For display: use totals if no company selected, otherwise use company-specific data
  const displayMonthlyData = !queryParams.companyId
    ? monthlyTotals.map(t => ({
        month: t.month,
        monthlyActiveUsers: t.totalMau,
        totalTransfers: t.totalTransfers,
        avgTransfersPerUser: t.avgTransfersPerUser,
        avgTransferAmount: t.avgTransferAmount,
        totalAmount: t.totalAmount,
      }))
    : filteredMonthlyData
        .sort((a, b) => a.month.localeCompare(b.month));

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#333' }}>
          📊 Engagement & Usage
        </h2>
        <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
          Measure how actively employees use Payro - frequency, volume, and engagement patterns
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '30px',
        }}
      >
        <MetricCard
          title="Active Users"
          value={Math.round(metrics.monthlyActiveUsers).toLocaleString()}
          subtitle="Users with transfers in period"
          color="#4CAF50"
        />
        <MetricCard
          title="Avg Transfers per User"
          value={metrics.avgTransfersPerUser || 0}
          subtitle="Per active user"
          color="#82ca9d"
        />
        <MetricCard
          title="Avg Transfer Amount"
          value={`₪${(metrics.avgTransferAmount || 0).toFixed(2)}`}
          subtitle="Per transfer"
          color="#ffc658"
        />
        <MetricCard
          title="Total Transfers"
          value={metrics.totalTransfers.toLocaleString()}
          subtitle="Successful transfers in period"
          color="#ff7300"
        />
      </div>

      <div style={{ marginBottom: '30px' }}>
        <LineChart
          data={mauTrend}
          title="Active Users Trend Over Time"
          color="#4CAF50"
          height={300}
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '20px',
          marginBottom: '30px',
        }}
      >
        <LineChart
          data={displayMonthlyData.map((row) => ({
            date: formatMonth(row.month),
            value: row.avgTransfersPerUser,
          }))}
          title="Average Transfers per User Trend (Monthly)"
          color="#82ca9d"
          height={300}
        />
        <div>
          <LineChart
            data={displayMonthlyData.map((row) => ({
              date: formatMonth(row.month),
              value: row.totalTransfers,
            }))}
            title="Total Transfers Trend (Monthly)"
            color="#ff7300"
            height={300}
          />
          <div style={{ 
            marginTop: '8px', 
            fontSize: '12px', 
            color: '#666', 
            textAlign: 'center',
            fontStyle: 'italic'
          }}>
            Shows the total count of successful transfers per month over time
          </div>
        </div>
      </div>


      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '20px',
          marginBottom: '30px',
        }}
      >
        <BarChart
          data={frequencyData.map((d) => ({
            transfers: d.transfers,
            users: d.userCount,
          }))}
          title="Transfer Frequency Distribution"
          dataKey="users"
          xAxisKey="transfers"
          color="#82ca9d"
          height={300}
        />
      </div>

      {/* Average Amount by Transfer Number */}
      <div style={{ marginBottom: '30px' }}>
        <LineChart
          data={averageAmountByTransferNumber.map((d) => ({
            date: `${d.transferNumber}${d.transferNumber === 1 ? 'st' : d.transferNumber === 2 ? 'nd' : d.transferNumber === 3 ? 'rd' : 'th'}`,
            value: d.averageAmount,
          }))}
          title="Average Transfer Amount by Transfer Number"
          color="#8884d8"
          height={400}
        />
        <div style={{ 
          marginTop: '8px', 
          fontSize: '12px', 
          color: '#666', 
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          Shows the average amount for each transfer number (1st transfer, 2nd transfer, etc.) for users who made at least that many transfers within the selected date range
        </div>
      </div>

      {/* Multi-Company Trends - Individual Company Lines */}
      {monthlyEngagementData.length > 0 && (() => {
        // Get selected company name from monthlyEngagementData
        const selectedCompanyName = queryParams.companyId
          ? monthlyEngagementData.find(d => d.companyId.toString() === queryParams.companyId)?.companyName
          : undefined;

        // Get all unique companies
        const companiesMap = new Map<number, { name: string; firstTransferMonth?: string }>();
        monthlyEngagementData.forEach(d => {
          if (!companiesMap.has(d.companyId)) {
            companiesMap.set(d.companyId, {
              name: d.companyName,
              firstTransferMonth: d.firstTransferMonth,
            });
          }
        });
        const companies = Array.from(companiesMap.values()).sort((a, b) => 
          (a.firstTransferMonth || '').localeCompare(b.firstTransferMonth || '')
        );

        // Get all unique months
        const monthsSet = new Set<string>();
        monthlyEngagementData.forEach(d => monthsSet.add(d.month));
        const allMonths = Array.from(monthsSet).sort();

        // Sort companies by their latest value (descending)
        // Get latest month's data for each company
        const latestMonth = allMonths[allMonths.length - 1];
        const companiesWithLatestValues = companies.map(company => {
          const latestData = monthlyEngagementData
            .filter(d => d.companyName === company.name && d.month === latestMonth)
            .sort((a, b) => b.month.localeCompare(a.month))[0];
          
          // For avg transfers chart, use avgTransfersPerUser; for velocity chart, use totalAmount
          // We'll calculate both and use the appropriate one when rendering
          return {
            ...company,
            latestAvgTransfers: latestData?.avgTransfersPerUser || 0,
            latestVelocity: latestData?.totalAmount || 0,
          };
        });

        // Generate colors for companies
        const colors = [
          '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1',
          '#d084d0', '#ffb347', '#87ceeb', '#da70d6', '#98d8c8',
          '#f7dc6f', '#bb8fce', '#85c1e2', '#f8b88b', '#a8e6cf',
          '#ffd3a5', '#fd79a8', '#fdcb6e', '#6c5ce7', '#a29bfe',
          '#fd79a8', '#fdcb6e', '#e17055', '#00b894', '#00cec9',
          '#0984e3', '#6c5ce7', '#a29bfe', '#dfe6e9', '#b2bec3',
        ];
        
        // Sort companies by latest value (descending) for avg transfers chart
        const companiesSortedByAvgTransfers = [...companiesWithLatestValues]
          .sort((a, b) => (b.latestAvgTransfers || 0) - (a.latestAvgTransfers || 0))
          .map((company, index) => ({
            name: company.name,
            color: colors[index % colors.length],
          }));
        
        // Sort companies by latest value (descending) for velocity chart
        const companiesSortedByVelocity = [...companiesWithLatestValues]
          .sort((a, b) => (b.latestVelocity || 0) - (a.latestVelocity || 0))
          .map((company, index) => ({
            name: company.name,
            color: colors[index % colors.length],
          }));

        // Transform data for avg transfers per user chart
        const avgTransfersData = allMonths.map(month => {
          const dataPoint: any = { date: formatMonth(month) };
          companies.forEach(company => {
            // Only show data from company's first transfer month onwards
            if (company.firstTransferMonth && month >= company.firstTransferMonth) {
              const companyData = monthlyEngagementData.find(
                d => d.month === month && d.companyName === company.name
              );
              dataPoint[company.name] = companyData?.avgTransfersPerUser || null;
            } else if (!company.firstTransferMonth) {
              // If no firstTransferMonth, show all data
              const companyData = monthlyEngagementData.find(
                d => d.month === month && d.companyName === company.name
              );
              dataPoint[company.name] = companyData?.avgTransfersPerUser || null;
            }
          });
          return dataPoint;
        });

        // Transform data for total velocity chart
        const velocityData = allMonths.map(month => {
          const dataPoint: any = { date: formatMonth(month) };
          companies.forEach(company => {
            // Only show data from company's first transfer month onwards
            if (company.firstTransferMonth && month >= company.firstTransferMonth) {
              const companyData = monthlyEngagementData.find(
                d => d.month === month && d.companyName === company.name
              );
              dataPoint[company.name] = companyData?.totalAmount || null;
            } else if (!company.firstTransferMonth) {
              // If no firstTransferMonth, show all data
              const companyData = monthlyEngagementData.find(
                d => d.month === month && d.companyName === company.name
              );
              dataPoint[company.name] = companyData?.totalAmount || null;
            }
          });
          return dataPoint;
        });

        return (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '20px',
              marginBottom: '30px',
            }}
          >
            <MultiCompanyLineChart
              data={avgTransfersData}
              title="Average Transfers per User Trend (All Companies)"
              companies={companiesSortedByAvgTransfers}
              height={600}
              selectedCompany={selectedCompanyName}
            />
            <MultiCompanyLineChart
              data={velocityData}
              title="Total Transfer Velocity Trend (All Companies)"
              companies={companiesSortedByVelocity}
              height={600}
              selectedCompany={selectedCompanyName}
            />
          </div>
        );
      })()}

      {/* Monthly Engagement Table */}
      {displayMonthlyData.length > 0 && (
        <div style={{
          backgroundColor: '#fff',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          marginBottom: '24px',
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
            📅 Monthly Engagement Breakdown
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#333' }}>Month</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Active Users</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Total Transfers</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Avg Transfers/User</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Avg Amount</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {displayMonthlyData.map((row, idx) => (
                  <tr key={`${row.month}-${idx}`} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px', color: '#333', fontWeight: 500 }}>
                      {formatMonth(row.month)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#333' }}>
                      {row.monthlyActiveUsers.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#333' }}>
                      {row.totalTransfers.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#333', fontWeight: 500 }}>
                      {row.avgTransfersPerUser.toFixed(2)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#333', fontWeight: 500 }}>
                      ₪{row.avgTransferAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#333', fontWeight: 500 }}>
                      ₪{row.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
